import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faMagnifyingGlass,
  faTriangleExclamation,
  faUserPlus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import toastService from '@frontend/AppSettings/services/ToastService';
import { parseLocalDate } from '@frontend/helpers/dateHelpers';
import TimeRequestService, { TimeRequest, TimeRequestRecipients } from './TimeRequestService';
import { formatRequestHours, isDayLockMessage, serverErrorMessage } from './dayLockHelpers';
import './TimesheetDayLocks.css';

interface TimeRequestsPanelProps {
  open: boolean;
  onClose: () => void;
  requests: TimeRequest[];
  /** The request an email deep link pointed at — highlighted and scrolled into view. */
  highlightRequestId?: string | null;
  /** The grid runs its own fetchData() + budget reload after an approval wrote a cell. */
  onApproved: (request: TimeRequest) => void | Promise<void>;
  /** Any decision (or a 409 race) — refresh the request queries. */
  onChanged: () => void;
  /** Clicking a card jumps the grid to that request's week (and opens its row). */
  onJumpToRequest?: (request: TimeRequest) => void;
  /** The server says this tab's lock state is stale — refetch the locks. */
  onStale?: () => void;
}

function isQuarterHours(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 24 && Math.abs(value * 4 - Math.round(value * 4)) < 1e-9;
}

const RequestCard: React.FC<{
  request: TimeRequest;
  highlighted: boolean;
  onApproved: TimeRequestsPanelProps['onApproved'];
  onChanged: () => void;
  onJumpToRequest?: (request: TimeRequest) => void;
  onStale?: () => void;
}> = ({ request, highlighted, onApproved, onChanged, onJumpToRequest, onStale }) => {
  const [hours, setHours] = useState(String(request.requestedHours));
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlighted) cardRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [highlighted]);

  const who = request.employeeName || 'An employee';
  const day = format(parseLocalDate(request.workDate), 'EEE MMM d');
  const changed =
    typeof request.liveHours === 'number' && Math.abs(request.liveHours - request.currentHours) > 1e-6;

  const fail = (err: unknown, fallback: string) => {
    const message = serverErrorMessage(err);
    toastService.error(message || fallback);
    if (isDayLockMessage(message)) onStale?.();
    onChanged();
  };

  const approve = async () => {
    const value = parseFloat(hours);
    if (!isQuarterHours(value)) {
      toastService.warning('Enter hours between 0 and 24 in quarter-hour steps (e.g. 1.25, 2.5).');
      return;
    }
    setBusy(true);
    try {
      const override = Math.abs(value - request.requestedHours) > 1e-9 ? value : undefined;
      const approved = await TimeRequestService.approve(request.requestId, override);
      toastService.success(`Approved — ${formatRequestHours(approved.approvedHours ?? value)} entered for ${who} on ${day}.`);
      await onApproved(approved);
      onChanged();
    } catch (err) {
      fail(err, 'Could not approve the request.');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await TimeRequestService.decline(request.requestId, reason.trim() || undefined);
      toastService.success(`Declined ${who}'s request for ${day}.`);
      onChanged();
    } catch (err) {
      fail(err, 'Could not decline the request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={cardRef}
      className={`tdl-card ${highlighted ? 'is-highlighted' : ''}`}
      data-request-id={request.requestId}
      onClick={() => onJumpToRequest?.(request)}
    >
      <div className="tdl-card-head">
        <span className="tdl-card-name">{who}</span>
        <span className="tdl-card-day">{day}</span>
      </div>
      <div className="tdl-card-meta" title={request.matterName ?? request.matterId}>
        {request.matterId}
        {request.matterName ? ` - ${request.matterName}` : ''} · code {request.costCode}
        {request.costCodeLabel ? ` ${request.costCodeLabel}` : ''}
      </div>
      <div className="tdl-card-hours" onClick={(e) => e.stopPropagation()}>
        <span className="tdl-card-current">{formatRequestHours(request.currentHours)}</span>
        <span aria-hidden="true">→</span>
        <input
          type="number"
          step="0.25"
          min="0"
          max="24"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          disabled={busy}
          aria-label={`Hours to approve for ${who} on ${day}`}
        />
        <span className="tdl-card-unit">hrs (new total)</span>
      </div>
      {changed && (
        <div className="tdl-card-changed" role="status">
          <FontAwesomeIcon icon={faTriangleExclamation} /> Changed since request:{' '}
          {formatRequestHours(request.currentHours)} → {formatRequestHours(request.liveHours)}
        </div>
      )}
      {request.note && <p className="tdl-card-note">“{request.note}”</p>}
      <div className="tdl-card-actions" onClick={(e) => e.stopPropagation()}>
        {declining ? (
          <>
            <input
              type="text"
              className="tdl-card-reason"
              placeholder="Reason (optional)"
              value={reason}
              maxLength={2000}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              aria-label="Reason for declining"
              autoFocus
            />
            <button type="button" className="tdl-btn tdl-btn-danger" onClick={decline} disabled={busy}>
              Confirm decline
            </button>
            <button type="button" className="tdl-btn" onClick={() => setDeclining(false)} disabled={busy}>
              Back
            </button>
          </>
        ) : (
          <>
            <button type="button" className="tdl-btn tdl-btn-primary" onClick={approve} disabled={busy}>
              {busy ? 'Saving…' : 'Approve'}
            </button>
            <button type="button" className="tdl-btn" onClick={() => setDeclining(true)} disabled={busy}>
              Decline
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/** The saved recipients that are still choosable, in the server's (name) order. */
function chosenAdmins(state: TimeRequestRecipients) {
  const wanted = new Set(state.recipients ?? []);
  return state.admins.filter((a) => wanted.has(a.employeeId));
}

/** The timesheet grid's avatar letter (GeneralTimesheetView): the name's first character. */
function initialOf(name: string): string {
  return (name.trim().charAt(0) || '?').toUpperCase();
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * Who gets the new-request email (plans/todo/time-request-email-recipients.md, decision g). Firm-wide;
 * the drawer is admin-only, and so are the endpoints. Opened from the add-person icon in the drawer
 * head and mounted only while open, so the choice is loaded fresh each time. It opens on the saved
 * choice (nobody ticked on the default, "All administrators", which is also what a fully stale choice
 * means). If the load fails it says so in one muted line; the request cards never depend on it.
 */
const RecipientsPicker: React.FC<{ id: string; onDone: () => void }> = ({ id, onDone }) => {
  const [state, setState] = useState<TimeRequestRecipients | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  // Ids ticked when the picker opened: listed first, so the saved choice is visible without
  // scrolling. Fixed while open, so a row never jumps while it is being ticked.
  const [pinned, setPinned] = useState<string[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);
  // False once the picker unmounts (icon toggled, drawer closed): a Save that resolves after that
  // must not close a picker reopened since, or pull focus back to the icon.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true; // StrictMode runs this twice; re-arm after its cleanup
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await TimeRequestService.getRecipients();
        if (!alive || !data || !Array.isArray(data.admins)) return;
        const ids = chosenAdmins(data).map((a) => a.employeeId);
        setState(data);
        setDraft(ids);
        setPinned(ids);
      } catch {
        if (alive) setLoadFailed(true);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, []);

  if (!state) {
    return (
      <section id={id} className="tdl-recipients" aria-label="Request email recipients" aria-busy={!loadFailed}>
        <p className="tdl-recipients-hint" role="status">
          {loadFailed ? 'Could not load who gets the request emails.' : 'Loading…'}
        </p>
      </section>
    );
  }
  const chosen = chosenAdmins(state);
  // Edit opens on the saved choice; on the default ("All administrators") it opens with nobody
  // ticked, so the admin picks people rather than unticking everyone else.
  const startIds = chosen.map((a) => a.employeeId);
  const allIds = state.admins.map((a) => a.employeeId);
  const allTicked = allIds.length > 0 && allIds.every((id) => draft.includes(id));
  // Every administrator ticked is the default (null), which also covers administrators added later.
  const target: string[] | null = allTicked ? null : draft;
  const unchanged =
    target === null
      ? state.recipients === null
      : state.recipients !== null && sameIds(target, startIds);
  // The search only narrows what is shown: a ticked administrator hidden by it stays ticked.
  const query = search.trim().toLowerCase();
  const ordered = [
    ...state.admins.filter((a) => pinned.includes(a.employeeId)),
    ...state.admins.filter((a) => !pinned.includes(a.employeeId)),
  ];
  const shown = query
    ? ordered.filter((a) => a.name.toLowerCase().includes(query) || a.email.toLowerCase().includes(query))
    : ordered;
  const shownIds = shown.map((a) => a.employeeId);
  // Select all ticks what the search shows (everyone when there is no search).
  const allShownTicked = shownIds.length > 0 && shownIds.every((id) => draft.includes(id));
  const clearSearch = () => {
    setSearch('');
    searchRef.current?.focus();
  };

  const save = async (ids: string[] | null) => {
    setBusy(true);
    try {
      const saved = await TimeRequestService.setRecipients(ids);
      const names = chosenAdmins(saved).map((a) => a.name);
      toastService.success(
        names.length ? `Request emails will go to ${names.join(', ')}.` : 'Request emails will go to all administrators.',
      );
      if (!mountedRef.current) return; // closed while saving: the save still happened, say so only
      setState(saved);
      onDone();
    } catch (err) {
      toastService.error(serverErrorMessage(err) || 'Could not save the email recipients.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const toggle = (employeeId: string) =>
    setDraft((ids) =>
      ids.includes(employeeId) ? ids.filter((x) => x !== employeeId) : [...ids, employeeId],
    );

  return (
    <section id={id} className="tdl-recipients" aria-label="Request email recipients">
      <div className="tdl-recipients-edit">
        <div className="tdl-recipients-head">
          <p className="tdl-recipients-title">Email new time requests to</p>
          {state.admins.length > 0 && (
            <span className="tdl-recipients-count" aria-live="polite">
              {draft.length} of {state.admins.length} selected
            </span>
          )}
        </div>
        {state.admins.length === 0 ? (
          <p className="tdl-recipients-hint">No administrator has an email address and can sign in.</p>
        ) : (
          <div className="tdl-recipients-picker">
            <div className="tdl-recipients-search">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="tdl-recipients-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="tdl-recipients-search-input"
                placeholder="Search by name or email"
                aria-label="Search administrators"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  // Escape clears the search first; the drawer's own Escape closes it only
                  // when the search is already empty.
                  if (e.key === 'Escape' && search) {
                    e.stopPropagation();
                    clearSearch();
                  }
                }}
                disabled={busy}
                ref={searchRef}
              />
              {search && (
                <button
                  type="button"
                  className="tdl-recipients-search-clear"
                  onClick={clearSearch}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                </button>
              )}
            </div>
            {shown.length === 0 ? (
              <p className="tdl-recipients-empty" role="status">No administrators match "{search.trim()}".</p>
            ) : (
              <ul className="tdl-recipients-list">
                {shown.map((a) => {
                  const ticked = draft.includes(a.employeeId);
                  return (
                    <li key={a.employeeId}>
                      <label className={`tdl-recipient${ticked ? ' is-ticked' : ''}`}>
                        <input
                          type="checkbox"
                          className="tdl-recipient-input"
                          checked={ticked}
                          onChange={() => toggle(a.employeeId)}
                          disabled={busy}
                          aria-label={`${a.name} — ${a.email}`}
                        />
                        {/* The avatar is the visible checkbox: the initial, or a check once ticked. */}
                        <span className="tdl-avatar" data-initial={ticked ? undefined : initialOf(a.name)} aria-hidden="true">
                          {ticked && <FontAwesomeIcon icon={faCheck} />}
                        </span>
                        <span className="tdl-recipient-text">
                          <span className="tdl-recipient-name">{a.name}</span>
                          {a.name !== a.email && (
                            <span className="tdl-recipient-email" title={a.email}>
                              {a.email}
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        <div className="tdl-recipients-actions">
          <button
            type="button"
            className="tdl-btn tdl-btn-primary"
            onClick={() => save(target)}
            disabled={busy || draft.length === 0 || unchanged}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="tdl-btn"
            onClick={() => setDraft((ids) => [...ids, ...shownIds.filter((id) => !ids.includes(id))])}
            disabled={busy || shownIds.length === 0 || allShownTicked}
          >
            Select all
          </button>
          <button
            type="button"
            className="tdl-btn tdl-recipients-cancel"
            onClick={onDone}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
};

/**
 * The admin Requests drawer (plan F1): one card per pending request, across every week. Approve
 * writes the cell server-side (adjust the hours on the card first if needed); Decline takes an
 * optional reason. Buttons are disabled while a decision is in flight.
 */
const TimeRequestsPanel: React.FC<TimeRequestsPanelProps> = ({
  open,
  onClose,
  requests,
  highlightRequestId,
  onApproved,
  onChanged,
  onJumpToRequest,
  onStale,
}) => {
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const recipientsBtnRef = useRef<HTMLButtonElement>(null);
  const pickerId = useId();
  // Closing the picker (Save or Cancel) hands focus back to the icon that opened it.
  const closeRecipients = useCallback(() => {
    setRecipientsOpen(false);
    recipientsBtnRef.current?.focus();
  }, []);
  useEffect(() => {
    if (!open) setRecipientsOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <aside className="tdl-panel" role="dialog" aria-label="Time requests">
      <div className="tdl-panel-head">
        <h3>Time requests ({requests.length})</h3>
        <div className="tdl-panel-head-actions">
          <button
            ref={recipientsBtnRef}
            type="button"
            className={`tdl-icon-btn${recipientsOpen ? ' is-active' : ''}`}
            onClick={() => setRecipientsOpen((v) => !v)}
            aria-label="Edit request email recipients"
            aria-expanded={recipientsOpen}
            aria-controls={recipientsOpen ? pickerId : undefined}
            title="Choose who gets request emails"
          >
            <FontAwesomeIcon icon={faUserPlus} aria-hidden="true" />
          </button>
          <button type="button" className="tdl-panel-close" onClick={onClose} aria-label="Close time requests">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>
      {recipientsOpen && <RecipientsPicker id={pickerId} onDone={closeRecipients} />}
      <div className="tdl-panel-body">
        {requests.length === 0 ? (
          <p className="tdl-panel-empty">No pending requests.</p>
        ) : (
          requests.map((r) => (
            <RequestCard
              key={r.requestId}
              request={r}
              highlighted={r.requestId === highlightRequestId}
              onApproved={onApproved}
              onChanged={onChanged}
              onJumpToRequest={onJumpToRequest}
              onStale={onStale}
            />
          ))
        )}
      </div>
    </aside>
  );
};

export default TimeRequestsPanel;
